import { useEffect, useId, useMemo, useState } from "react";
import { type AntibioticPanels, cultureShape, EMPTY_ANTIBIOTIC_PANELS, nameKey, PANEL_LABEL, type PanelGram, type StudyDetail } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { useDragReorder } from "../lib/useDragReorder.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";
import { Icon } from "./icons.js";
import { Button, Card, IconButton, Select } from "./ui.js";

/*
 * Catálogo → Antibióticos (ADR 0026; the lab, 2026-11-03 08:37: "el apartado de Gram positivo y
 * Gram negativo y que cada uno tenga su lista de antibióticos"). Two lists, one per Gram, in the
 * order they print. When the chemist classifies an organism of a culture, its antibiogram starts
 * with that list. An Administrador adds, removes, reorders (handle or arrows, as Catálogo → Áreas)
 * and can start a list from one of the laboratory's own culture studies; everyone else reads.
 * Changes are local until «Guardar lista»; orders already captured keep their antibiograms.
 */

/** The antibiotics of a culture study, in its print order (active rows only). */
export function studyAntibiotics(study: Pick<StudyDetail, "analytes">): string[] {
  const active = study.analytes.filter((a) => a.is_active).sort((a, b) => a.sort_order - b.sort_order);
  return (cultureShape(active)?.antibiotics ?? []).map((a) => a.name.replace(/\s+/g, " ").trim());
}

/** Culture studies of the catalogue with their antibiotics, for «Copiar de un estudio» and the suggestions. */
export function cultureStudies(studies: StudyDetail[]): { id: string; clave: string; name: string; antibiotics: string[] }[] {
  return studies
    .filter((s) => s.is_active)
    .map((s) => ({ id: s.id, clave: s.clave, name: s.name, antibiotics: studyAntibiotics(s) }))
    .filter((s) => s.antibiotics.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Unique by name (case and accents ignored), first one kept. */
export function uniqueNames(names: string[]): string[] {
  const seen = new Set<string>();
  return names.filter((n) => {
    const k = nameKey(n);
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function AntibioticPanelsEditor({ panels, studies, admin, onChanged }: { panels: AntibioticPanels | undefined; studies: StudyDetail[]; admin: boolean; onChanged: () => void }) {
  const p = panels ?? EMPTY_ANTIBIOTIC_PANELS;
  const cultures = useMemo(() => cultureStudies(studies), [studies]);
  const known = useMemo(() => uniqueNames([...p.negative, ...p.positive, ...cultures.flatMap((c) => c.antibiotics)]).sort((a, b) => a.localeCompare(b)), [p, cultures]);
  return (
    <div className="stack" style={{ gap: 16 }}>
      <p className="small muted">
        Cuando el químico elige el Gram de un microorganismo en un cultivo, su antibiograma empieza con esta lista, en este orden. Puede agregar o quitar antibióticos en cada solicitud; las solicitudes ya capturadas no cambian.
      </p>
      <div className="panel-grid">
        {(["negative", "positive"] as const).map((gram) => (
          <PanelCard key={gram} gram={gram} server={p[gram]} known={known} cultures={cultures} admin={admin} onChanged={onChanged} />
        ))}
      </div>
    </div>
  );
}

function PanelCard({ gram, server, known, cultures, admin, onChanged }: { gram: PanelGram; server: string[]; known: string[]; cultures: ReturnType<typeof cultureStudies>; admin: boolean; onChanged: () => void }) {
  const toast = useToast();
  const listId = useId();
  const serverKey = JSON.stringify(server);
  const [order, setOrder] = useState<string[]>(server);
  useEffect(() => setOrder(JSON.parse(serverKey) as string[]), [serverKey]);
  const dirty = JSON.stringify(order) !== serverKey;
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [copyFrom, setCopyFrom] = useState("");
  const { dragId, rowRef, gripProps, step } = useDragReorder(setOrder);
  const title = `Antibióticos ${PANEL_LABEL[gram]}`;
  const typed = name.replace(/\s+/g, " ").trim();
  const inList = new Set(order.map(nameKey));

  const add = () => {
    if (!typed || inList.has(nameKey(typed))) return;
    setOrder([...order, typed]);
    setName("");
  };
  const copy = (studyId: string) => {
    const s = cultures.find((c) => c.id === studyId);
    if (s) setOrder(uniqueNames(s.antibiotics).slice(0, 60));
    setCopyFrom("");
  };

  async function save() {
    setBusy(true);
    try {
      await run("catalogue.antibiotic_panel.set", { gram, antibiotics: order });
      toast.success(`Lista de ${PANEL_LABEL[gram]} guardada.`);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={title}
      pad={false}
      actions={
        admin &&
        dirty && (
          <span className="row" style={{ gap: 8 }}>
            <Button variant="ghost" onClick={() => setOrder(JSON.parse(serverKey) as string[])} disabled={busy}>
              Descartar
            </Button>
            <Button variant="primary" icon="check" busy={busy} onClick={() => void save()}>
              Guardar lista
            </Button>
          </span>
        )
      }
    >
      {order.length === 0 ? (
        <p className="small muted" style={{ padding: "14px 18px" }}>
          {admin ? "Todavía no hay antibióticos en esta lista. Agrégalos abajo o cópialos de uno de tus cultivos." : "Todavía no hay antibióticos en esta lista."}
        </p>
      ) : (
        <div role="list" aria-label={title}>
          {order.map((a, i) => (
            <div key={a} role="listitem" ref={rowRef(a)} className={`list-row${dragId === a ? " dragging" : ""}`}>
              {admin && (
                <span className="drag-handle" aria-hidden="true" title="Arrastrar" {...gripProps(a)}>
                  <Icon name="grip" />
                </span>
              )}
              <span className="num" style={{ width: 32, color: "var(--amber)" }}>
                {i + 1}
              </span>
              <span className="grow primary">{a}</span>
              {admin && (
                <span className="row" style={{ gap: 2 }}>
                  <IconButton icon="arrowUp" label={`Subir ${a}`} disabled={i === 0 || busy} onClick={() => step(a, -1)} />
                  <IconButton icon="arrowDown" label={`Bajar ${a}`} disabled={i === order.length - 1 || busy} onClick={() => step(a, 1)} />
                  <IconButton icon="trash" label={`Quitar ${a}`} disabled={busy} onClick={() => setOrder(order.filter((x) => x !== a))} />
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {admin && (
        <div className="stack panel-tools" style={{ gap: 10, padding: "14px 18px", borderTop: "1px solid var(--line-soft)" }}>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input grow"
              list={listId}
              aria-label={`Agregar a ${PANEL_LABEL[gram]}`}
              placeholder="Agregar antibiótico (p. ej. Ampicilina 10 µg)"
              value={name}
              maxLength={120}
              {...NO_AUTOFILL}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
            />
            <Button variant="secondary" icon="plus" disabled={!typed || inList.has(nameKey(typed)) || order.length >= 60} onClick={add}>
              Agregar
            </Button>
          </div>
          <datalist id={listId}>
            {known
              .filter((k) => !inList.has(nameKey(k)))
              .map((k) => (
                <option key={k} value={k} />
              ))}
          </datalist>
          {cultures.length > 0 && (
            <Select aria-label={`Copiar ${PANEL_LABEL[gram]} de un estudio`} value={copyFrom} onChange={(e) => copy(e.target.value)}>
              <option value="">Copiar de un estudio…</option>
              {cultures.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.clave}) · {c.antibiotics.length} antibióticos
                </option>
              ))}
            </Select>
          )}
          {dirty && <p className="small muted" style={{ margin: 0 }}>Sin guardar. Se aplica a los cultivos que se capturen después de guardarla.</p>}
        </div>
      )}
    </Card>
  );
}
